import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Checkbox,
  Form,
  Icon,
  Input,
  Modal,
} from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  clientError, firstError, firstResponseError, pruneServerErrors,
  validationClasses, validateIpRange,
} from '@/utils/errors';
import { hasMinimumLength } from '@/utils/resultsHelper';
import parseDate, { formatDateForState } from '@/utils/dateHelper';
import fields, { globalFieldLengths } from '@/constants/fields';
import { states } from '@/constants/lifecycle';
import Actions from '@/components/buttons/Actions';
import FormValidationError from '@/components/errors/FormValidationError';
import DialogWrapper from '@/components/dialogs/DialogWrapper';
import RolesDropdown from '@/roles/containers/RolesDropdown';
import CopyButton from '@/components/buttons/CopyButton';
import MarkdownEditor from '@/components/text-editors/MarkdownEditor';
import { hideSaveToken, createToken, updateActiveTokenProperties } from '../actions';

const SaveTokenDialog = ({
  createErrors,
  createStatus,
  description,
  expiresOn,
  hide,
  ipRange,
  jwt,
  maxTokenLength,
  maxUses,
  name,
  postData,
  role,
  updateProperties,
  theme,
}) => {
  const intl = useIntl();
  const titleText = () => (createStatus === states.SUCCESS
    ? intl.formatMessage({ id: 'save.token.created.title' })
    : intl.formatMessage({ id: 'save.token.create.title' }));

  const validateForm = () => {
    const validationErrors = [];

    if (!hasMinimumLength(name)) {
      validationErrors.push(
        clientError(
          { entity: 'token' },
          intl.formatMessage({ id: 'save.token.validation.name.title' }),
          intl.formatMessage({ id: 'validation.name.required' }),
          [fields.NAME],
        ),
      );
    }

    if (name && name.length > globalFieldLengths.name) {
      validationErrors.push(
        clientError(
          { entity: 'token' },
          intl.formatMessage({ id: 'save.token.validation.name.title' }),
          intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.name }),
          [fields.NAME],
        ),
      );
    }

    if (role.trim() === '') {
      validationErrors.push(
        clientError(
          { entity: 'token' },
          intl.formatMessage({ id: 'save.token.validation.role.title' }),
          intl.formatMessage({ id: 'validation.role.required' }),
          [fields.ROLE],
        ),
      );
    }

    if (ipRange && ipRange.length > globalFieldLengths.ipRange) {
      validationErrors.push(
        clientError(
          { entity: 'token' },
          intl.formatMessage({ id: 'save.token.validation.ip.range.title' }),
          intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.ipRange }),
          [fields.IP_RANGE],
        ),
      );
    }

    if (ipRange && !validateIpRange(ipRange)) {
      validationErrors.push(
        clientError(
          { entity: 'token' },
          intl.formatMessage({ id: 'save.token.validation.ip.range.title' }),
          intl.formatMessage({ id: 'validation.invalid.ip.range' }),
          [fields.IP_RANGE],
        ),
      );
    }

    if (!hasMinimumLength(description)) {
      validationErrors.push(
        clientError(
          { entity: 'token' },
          intl.formatMessage({ id: 'save.token.validation.description.title' }),
          intl.formatMessage({ id: 'validation.reason.required' }),
          [fields.DESCRIPTION],
        ),
      );
    }

    if (description && description.length > globalFieldLengths.description) {
      validationErrors.push(
        clientError(
          { entity: 'token' },
          intl.formatMessage({ id: 'save.token.validation.description.title' }),
          intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.description }),
          [fields.DESCRIPTION],
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    updateProperties({ createErrors: validationErrors });

    return validationErrors;
  };

  const submitForm = () => {
    const validationErrors = checkForErrors();

    if (validationErrors.length === 0) {
      postData();
    }
  };

  const nameError = createErrors.find((e) => (e.fields.includes(fields.NAME)));
  const roleError = createErrors.find((e) => (e.fields.includes(fields.ROLE)));
  const descriptionError = createErrors.find((e) => (e.fields.includes(fields.DESCRIPTION)));
  const ipRangeError = createErrors.find((e) => (e.fields.includes(fields.IP_RANGE)));
  const dateError = createErrors.find((e) => (e.fields.includes(fields.DATE)));

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + maxTokenLength);

  return (
    <DialogWrapper
      className="aridhia-save-token-dialog"
      error={firstResponseError(createErrors)}
      icon="code"
      onClose={hide}
      onDismiss={() => updateProperties({ createErrors: pruneServerErrors(createErrors) })}
      theme={theme}
      title={titleText()}
    >
      <Modal.Content scrolling>
        {createStatus === states.SUCCESS && (
          <div className="aridhia-about-dialog-access-container">
            <FormattedMessage id="about.token" />
            <Input
              fluid
              labelPosition="right"
              label={<CopyButton value={jwt} message="token.copy" />}
              value={jwt}
            />
          </div>
        )}
        {createStatus !== states.SUCCESS && (
          <Form autoComplete="off" onSubmit={submitForm} error={firstError(createErrors) !== null}>
            <FormValidationError
              error={nameError}
              htmlFor="token_name"
              labelId="save.token.name"
              position="right"
            >
              <Input
                id="token_name"
                autoFocus
                disabled={createStatus === states.PENDING}
                type="custom"
                size="large"
                placeholder={intl.formatMessage({ id: 'save.token.name.placeholder' })}
                value={name}
                maxLength={globalFieldLengths.name}
                onChange={({ target }) => updateProperties({ name: target.value })}
                className={validationClasses(nameError)}
              />
            </FormValidationError>
            <FormValidationError
              error={roleError}
              htmlFor="role_dropdown"
              labelId="save.token.role"
              position="right"
            >
              <RolesDropdown
                category="tokens"
                value={role}
                disabled={createStatus === states.PENDING}
                onChange={(e, { value }) => updateProperties({ role: value })}
                withDescription
                withPermissions
                fluid
              />
            </FormValidationError>
            <FormValidationError
              error={dateError}
              htmlFor="token_expires"
              labelId="save.token.expires"
              position="right"
            >
              <DatePicker
                name="token_expires"
                fixedHeight
                minDate={new Date()}
                maxDate={maxDate}
                dateFormat="dd/MMM/yyyy"
                selected={parseDate(expiresOn)}
                disabled={createStatus === states.PENDING}
                placeholderText={intl.formatMessage({ id: 'save.token.expires.placeholder' })}
                onChange={(value) => updateProperties({ expiresOn: formatDateForState(value) })}
                className={validationClasses(dateError)}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </FormValidationError>
            <FormValidationError
              error={ipRangeError}
              htmlFor="token_ip_range"
              labelId="save.token.ip.range"
            >
              <Input
                id="token_ip_range"
                placeholder={intl.formatMessage({ id: 'save.token.ip.range.placeholder' })}
                disabled={createStatus === states.PENDING}
                value={ipRange}
                onChange={({ target }) => updateProperties({ ip_range: target.value })}
                type="custom"
                size="large"
                maxLength={globalFieldLengths.ipRange}
                className={validationClasses(ipRangeError)}
              />
            </FormValidationError>
            <Form.Field>
              <Checkbox
                id="token_max_uses"
                checked={maxUses}
                label={intl.formatMessage({ id: 'save.token.max.uses' })}
                onClick={() => updateProperties({ max_uses: maxUses ? null : 1 })}
                toggle
                className="aridhia-form-toggle"
              />
            </Form.Field>
            <FormValidationError
              error={descriptionError}
              htmlFor="token_description"
              labelId="save.token.description"
            >
              <MarkdownEditor
                id="token_description"
                disabled={createStatus === states.PENDING}
                value={description}
                onChange={(value) => updateProperties({ description: value })}
                maxEditorHeight={80}
                minPreviewHeight={94}
                className={validationClasses(descriptionError)}
              />
            </FormValidationError>
          </Form>
        )}
      </Modal.Content>
      {createStatus === states.SUCCESS && (
      <Modal.Actions>
        <CopyToClipboard text={jwt}>
          <Button
            icon
            labelPosition="right"
            positive
            onClick={hide}
          >
            <FormattedMessage id="save.token.copy.button" />
            <Icon name="copy" />
          </Button>
        </CopyToClipboard>
      </Modal.Actions>
      )}
      {createStatus !== states.SUCCESS && (
      <Actions
        onClose={hide}
        inProgress={createStatus === states.PENDING}
        onSave={submitForm}
        minimumDataEntryMet={name && description && role}
        saveMessageId="save.token.create.button"
      />
      )}
    </DialogWrapper>
  );
};

const mapStateToProps = ({
  tokens: { activeToken },
  auth: { role },
}) => ({
  createErrors: activeToken.createErrors,
  createStatus: activeToken.createStatus,
  description: activeToken.description,
  expiresOn: activeToken.expiresOn,
  ipRange: activeToken.ip_range,
  jwt: activeToken.jwt,
  maxTokenLength: role.max_token_length_days,
  maxUses: activeToken.max_uses,
  name: activeToken.name,
  role: activeToken.role,
});

const mapDispatchToProps = {
  hide: hideSaveToken,
  updateProperties: updateActiveTokenProperties,
  postData: createToken,
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveTokenDialog);
